"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { type ApiProfileWaveResponse, clearProfileWave, setProfileWave } from "@/services/api/profile-wave-api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { getProfileWaveIdentity, setProfileWaveQueryData } from "./useProfileWave";
type ProfileWaveAction = {
  readonly type: "set";
  readonly waveId: string;
  readonly profileCurationId?: string | null | undefined;
} | {
  readonly type: "clear";
};
export function useProfileWaveMutation(profile) {
  const $ = _c(27);
  const queryClient = useQueryClient();
  const {
    requestAuth,
    setToast
  } = useAuth();
  const {
    onProfileEdit
  } = useContext(ReactQueryWrapperContext);
  let t0;
  if ($[0] !== profile) {
    t0 = async action => {
      const identity = getProfileWaveIdentity(profile);
      if (identity.length === 0) {
        throw new Error("Unable to determine the profile identity.");
      }
      if (action.type === "set") {
        return await setProfileWave({
          identity,
          waveId: action.waveId,
          profileCurationId: action.profileCurationId
        });
      }
      return await clearProfileWave({
        identity
      });
    };
    $[0] = profile;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== onProfileEdit || $[3] !== profile || $[4] !== queryClient || $[5] !== setToast) {
    t1 = (updatedProfile, action_0) => {
      onProfileEdit({
        profile: updatedProfile,
        previousProfile: profile
      });
      const profileWaveData = {
        profile_wave_id: action_0.type === "set" ? action_0.waveId : updatedProfile.profile_wave_id,
        profile_curation_id: action_0.type === "set" ? action_0.profileCurationId ?? null : null
      };
      setProfileWaveQueryData(queryClient, [profile, updatedProfile], profileWaveData);
      setToast({
        message: action_0.type === "set" ? "Profile wave updated." : "Profile wave cleared.",
        type: "success"
      });
    };
    $[2] = onProfileEdit;
    $[3] = profile;
    $[4] = queryClient;
    $[5] = setToast;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== setToast) {
    t2 = (error, action_1) => {
      const fallbackMessage = action_1.type === "set" ? "Unable to update profile wave." : "Unable to clear profile wave.";
      setToast({
        message: error instanceof Error ? error.message : fallbackMessage,
        type: "error"
      });
    };
    $[7] = setToast;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t0 || $[10] !== t1 || $[11] !== t2) {
    t3 = {
      mutationFn: t0,
      onSuccess: t1,
      onError: t2
    };
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const mutation = useMutation(t3);
  let t4;
  if ($[13] !== requestAuth) {
    t4 = async () => {
      const {
        success
      } = await requestAuth();
      return success;
    };
    $[13] = requestAuth;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const ensureAuthenticated = t4;
  let t5;
  if ($[15] !== ensureAuthenticated || $[16] !== mutation) {
    t5 = async action_2 => {
      if (!(await ensureAuthenticated())) {
        return null;
      }
      try {
        return await mutation.mutateAsync(action_2);
      } catch {
        return null;
      }
    };
    $[15] = ensureAuthenticated;
    $[16] = mutation;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const runProfileWaveMutation = t5;
  let t6;
  if ($[18] !== runProfileWaveMutation) {
    t6 = async (waveId, profileCurationId) => await runProfileWaveMutation({
      type: "set",
      waveId,
      profileCurationId
    });
    $[18] = runProfileWaveMutation;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const updateProfileWave = t6;
  let t7;
  if ($[20] !== runProfileWaveMutation) {
    t7 = async () => await runProfileWaveMutation({
      type: "clear"
    });
    $[20] = runProfileWaveMutation;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const clearSelectedProfileWave = t7;
  const t8 = mutation.variables?.type ?? null;
  let t9;
  if ($[22] !== clearSelectedProfileWave || $[23] !== mutation.isPending || $[24] !== t8 || $[25] !== updateProfileWave) {
    t9 = {
      updateProfileWave,
      clearSelectedProfileWave,
      isPending: mutation.isPending,
      pendingAction: t8
    };
    $[22] = clearSelectedProfileWave;
    $[23] = mutation.isPending;
    $[24] = t8;
    $[25] = updateProfileWave;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}